import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw, Database, Clock, AlertTriangle, CheckCircle, XCircle, LogIn,
} from 'lucide-react';
import clsx from 'clsx';

const OPS_AUTH_KEY = 'propscope_ops_auth';

function getAuthHeader() {
  const token = sessionStorage.getItem(OPS_AUTH_KEY);
  return token ? { Authorization: `Basic ${token}` } : {};
}

function encodeBasicAuth(username, password) {
  const raw = `${username}:${password}`;
  const bytes = new TextEncoder().encode(raw);
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary);
}

async function opsFetch(path) {
  const res = await fetch(`/api/ops${path}`, { headers: { ...getAuthHeader() } });
  if (res.status === 401) {
    const err = new Error('Invalid username or password');
    err.status = 401;
    throw err;
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || `Request failed (${res.status})`;
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

function FlagBadge({ flag }) {
  const map = {
    ok: { label: 'OK', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    stale: { label: 'Stale', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    never: { label: 'Never', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
    missing: { label: 'Missing', cls: 'bg-slate-700 text-slate-400 border-slate-600' },
    success: { label: 'Success', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
    skipped: { label: 'Skipped', cls: 'bg-slate-700 text-slate-300 border-slate-600' },
    running: { label: 'Running', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  };
  const { label, cls } = map[flag] || { label: flag || '—', cls: 'bg-slate-800 text-slate-400 border-slate-700' };
  return (
    <span className={clsx('text-xs font-medium px-2 py-0.5 rounded border', cls)}>{label}</span>
  );
}

function fmtDt(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function LoginPanel({ onLogin }) {
  const [username, setUsername] = useState('admin');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const user = username.trim();
    const pass = password;
    const token = encodeBasicAuth(user, pass);
    sessionStorage.setItem(OPS_AUTH_KEY, token);
    try {
      await opsFetch('/ping');
      onLogin();
    } catch (err) {
      sessionStorage.removeItem(OPS_AUTH_KEY);
      if (err.status === 401) {
        setError('Invalid username or password');
      } else if (err.status === 503) {
        setError('Ops admin is not configured on this server (OPS_ADMIN_PASSWORD missing).');
      } else {
        setError(err.message || 'Could not reach the ops API');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-slate-900 border border-slate-800 rounded-2xl p-8">
        <h1 className="text-lg font-semibold text-white mb-1 text-center">PropScope Ops</h1>
        <p className="text-slate-500 text-xs text-center mb-6">Data ingestion &amp; freshness (read-only)</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Username</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white"
            />
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg py-2.5 font-medium flex items-center justify-center gap-2"
          >
            <LogIn size={16} />
            {loading ? 'Signing in…' : 'View status'}
          </button>
        </form>
        <p className="text-center text-slate-600 text-xs mt-4">
          <Link to="/" className="hover:text-slate-400">← Back to PropScope</Link>
        </p>
      </div>
    </div>
  );
}

export default function AdminOps() {
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem(OPS_AUTH_KEY));
  const [status, setStatus] = useState(null);
  const [cronLogs, setCronLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, c] = await Promise.all([
        opsFetch('/status'),
        opsFetch('/cron-logs'),
      ]);
      setStatus(s);
      setCronLogs(c);
      setAuthed(true);
    } catch (err) {
      if (err.status === 401) {
        sessionStorage.removeItem(OPS_AUTH_KEY);
        setAuthed(false);
      } else {
        setError(err.message || 'Failed to load status');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const logout = () => {
    sessionStorage.removeItem(OPS_AUTH_KEY);
    setAuthed(false);
    setStatus(null);
    setCronLogs(null);
  };

  if (!authed) {
    return <LoginPanel onLogin={() => setAuthed(true)} />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="border-b border-slate-800 px-6 py-4">
        <div className="max-w-6xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-white flex items-center gap-2">
              <Database size={18} className="text-emerald-400" />
              PropScope Ops — Data Freshness
            </h1>
            <p className="text-slate-500 text-xs mt-0.5">
              Last checked {status ? fmtDt(status.checked_at) : '—'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={loading}
              className="text-sm px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center gap-1.5"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button onClick={logout} className="text-sm px-3 py-1.5 rounded-lg text-slate-400 hover:text-white">
              Sign out
            </button>
            <Link to="/" className="text-sm text-slate-500 hover:text-slate-300">Home</Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm flex gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {status?.env_checks && (
          <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <EnvCard
              label="EPC_BEARER_TOKEN"
              ok={status.env_checks.epc_bearer_token_set}
              detail={status.env_checks.epc_bearer_token_set ? 'Set on API host' : 'Missing — EPC daily import will fail'}
            />
            <EnvCard
              label="reCAPTCHA"
              ok={status.env_checks.recaptcha_configured}
              detail={status.env_checks.recaptcha_configured ? 'Configured' : 'Not configured'}
            />
          </section>
        )}

        <section>
          <SectionTitle icon={Clock} title="ETL manifest (ingestion jobs)" />
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Dataset</th>
                  <th className="text-left px-4 py-3">Cadence</th>
                  <th className="text-left px-4 py-3">Last success</th>
                  <th className="text-left px-4 py-3">Age</th>
                  <th className="text-left px-4 py-3">Last run</th>
                  <th className="text-left px-4 py-3">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {(status?.manifest || []).map((row) => (
                  <tr key={row.key} className="hover:bg-slate-900/50">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{row.label}</div>
                      <div className="text-slate-500 text-xs font-mono">{row.key}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-400">{row.cadence}</td>
                    <td className="px-4 py-3">{fmtDt(row.last_success_at)}</td>
                    <td className="px-4 py-3">
                      <FlagBadge flag={row.freshness_flag} />
                      {row.last_success_days_ago != null && (
                        <span className="text-slate-500 text-xs ml-2">{row.last_success_days_ago}d</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <FlagBadge flag={row.last_ingestion_status || 'never'} />
                    </td>
                    <td className="px-4 py-3 text-red-400 text-xs max-w-xs truncate" title={row.last_error || ''}>
                      {row.last_error || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <SectionTitle icon={Database} title="Live data in database" />
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-left px-4 py-3">Latest data</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Rows</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {(status?.sources || []).map((row) => (
                  <tr key={row.key} className="hover:bg-slate-900/50">
                    <td className="px-4 py-3">
                      <div className="text-white">{row.label}</div>
                      <div className="text-slate-500 text-xs">{row.category}</div>
                    </td>
                    <td className="px-4 py-3">{row.last_data_date || '—'}</td>
                    <td className="px-4 py-3">
                      <FlagBadge flag={row.freshness_flag} />
                      {row.last_data_days_ago != null && (
                        <span className="text-slate-500 text-xs ml-2">{row.last_data_days_ago}d ago</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-300">
                      {row.row_count?.toLocaleString?.() ?? row.row_count ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <SectionTitle icon={CheckCircle} title="Recent ingestion runs" />
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-slate-400 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Dataset</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Detail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {(status?.recent_ingestions || []).map((row) => (
                  <tr key={row.id} className="hover:bg-slate-900/50">
                    <td className="px-4 py-3 whitespace-nowrap">{fmtDt(row.started_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{row.dataset_key}</td>
                    <td className="px-4 py-3"><FlagBadge flag={row.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-400 max-w-lg truncate" title={row.error_message || row.log_excerpt || ''}>
                      {row.error_message || row.log_excerpt || '—'}
                    </td>
                  </tr>
                ))}
                {!status?.recent_ingestions?.length && (
                  <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-500">No ingestion runs logged yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <SectionTitle icon={Clock} title="Cron log files" />
          <p className="text-slate-500 text-xs mb-3">From {cronLogs?.log_dir || '/app/logs'} on the scheduler host</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(cronLogs?.cron_logs || []).map((row) => (
              <div key={row.key} className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="text-white text-sm font-medium">{row.key}</span>
                  <FlagBadge flag={row.status} />
                </div>
                <p className="text-slate-500 text-xs mb-2">{row.schedule}</p>
                <p className="text-slate-400 text-xs">
                  {row.age_hours != null ? `${row.age_hours}h ago` : 'log missing'}
                </p>
                {row.tail?.length > 0 && (
                  <pre className="mt-2 text-[10px] text-slate-500 bg-slate-950 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                    {row.tail.join('\n')}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }) {
  return (
    <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-3 flex items-center gap-2">
      <Icon size={15} className="text-emerald-400" />
      {title}
    </h2>
  );
}

function EnvCard({ label, ok, detail }) {
  return (
    <div className={clsx(
      'rounded-xl border p-4 flex items-start gap-3',
      ok ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5',
    )}>
      {ok ? <CheckCircle size={18} className="text-emerald-400 shrink-0" /> : <XCircle size={18} className="text-amber-400 shrink-0" />}
      <div>
        <p className="text-white text-sm font-medium">{label}</p>
        <p className="text-slate-400 text-xs mt-0.5">{detail}</p>
      </div>
    </div>
  );
}
